import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Admin/Sidebar';
import { FiCalendar, FiPlus, FiSearch, FiCheck, FiX, FiClock } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ncb from '../../db/ncb';

const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchBookings = () => {
    setLoading(true);
    const data = ncb.bookings.list();
    setBookings(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'confirmed': return FiCheck;
      case 'pending': return FiClock;
      case 'cancelled': return FiX;
      default: return FiCalendar;
    }
  };

  const filteredBookings = filter === 'all' ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div className="flex bg-brand-cream min-h-screen">
      <Sidebar />
      <main className="flex-grow p-8">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="font-serif text-3xl text-brand-dark font-bold">Bookings Management</h1>
            <p className="text-brand-sage text-sm">Review and manage guest reservations.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-light" />
              <input type="text" placeholder="Search guests..." className="pl-10 pr-4 py-2 bg-white border border-brand-light/30 rounded-sm text-sm focus:outline-none focus:border-brand-olive w-64" />
            </div>
            <button className="bg-brand-olive text-white px-6 py-2.5 rounded-sm flex items-center gap-2 uppercase tracking-widest text-[10px] font-bold hover:bg-brand-dark transition-all shadow-lg">
              <SafeIcon icon={FiPlus} /> Manual Booking
            </button>
          </div>
        </header>

        <div className="flex gap-2 mb-8 bg-white p-1 rounded-sm border border-brand-light/20 w-fit">
          {['all', 'pending', 'confirmed', 'cancelled'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-6 py-2 text-[10px] uppercase tracking-widest font-bold rounded-sm transition-all ${filter === f ? 'bg-brand-olive text-white shadow-md' : 'text-brand-sage hover:bg-brand-cream'}`}>
              {f}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-sm border border-brand-light/30 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-brand-cream/50 text-brand-sage text-[10px] uppercase tracking-[0.2em] font-bold border-b border-brand-light/20">
                  <th className="px-6 py-5">Guest Information</th>
                  <th className="px-6 py-5">Check-in / Out</th>
                  <th className="px-6 py-5">Status</th>
                  <th className="px-6 py-5">Occupancy</th>
                  <th className="px-6 py-5 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-light/10">
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-brand-cream/10 transition-colors group">
                    <td className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="font-bold text-brand-dark text-sm">{booking.guest_name}</span>
                        <span className="text-xs text-brand-sage">{booking.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-col text-xs">
                        <span className="text-brand-dark font-medium">{booking.check_in}</span>
                        <span className="text-brand-sage">to {booking.check_out}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-tighter ${getStatusColor(booking.status)}`}>
                        <SafeIcon icon={getStatusIcon(booking.status)} /> {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-xs text-brand-dark">{booking.guests} Guests</span>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <span className="font-bold text-brand-olive">{booking.total}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredBookings.length === 0 && (
            <div className="py-20 text-center">
              <SafeIcon icon={FiCalendar} className="text-4xl text-brand-light mx-auto mb-4" />
              <p className="text-brand-sage italic">No bookings found.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BookingsManagement;